// The Mission Panel card's nine capability switches as data (#6766, epic #6552; prototype
// capState / capStatus / holdAttr `:2108-2120`, capGrid / onCount `:2296-2304`,
// `queue` / `commit` `:2011-2063`). Pure: no signal, no DOM, no clock. The card's controller
// (`./capability-switches.ts`) feeds it the read and the client's facts and renders the view.
//
// STATE COMES FROM THE SERVER (#6757's per-machine `roles[]`). `machine_enabled` is the
// machine's own switch, `running` the jobs it holds under the key, `blocked` why an allowed
// role still does not run there (the org's off, a box that cannot converse, a build that
// cannot run it). A machine switch is ANDed with the org/repo setting, so a blocked role
// still shows the machine's OWN setting, plus its reason.
//
// THE SHOWN VALUE of a switch is the newest of, in order: a change waiting in the page's
// flood guard or in flight (`wanted`, shown at once), then what an answer said, held only
// until a fleet read that STARTED after the answer lands (`answer`, compared in the page's
// order, `./read-order.ts`, never by the clock), then the read's `machine_enabled`. A
// finishing role ("then off") is one shown off while it still runs jobs, and reads checked,
// as the prototype's onCount counts it.
//
// "SAVED" WAITS FOR A FRESH READ. A 2xx is what the server did, not what the machine now
// reads: the switch stays busy ("Saving…") from the send until the first read later than the
// answer, and only a read that CONFIRMS the value says "Saved" and announces it
// ({@link settleCaps}). A read that disagrees wins and says what it shows instead.
//
// THE LAST ROLE ON mirrors the server's 409 `last_role_on` (`machineSwitchVerdict`,
// `@developerz/domain`): over the SHOWN values, a work role is last when turning it off would
// leave every work key off. Escalation decorates work and never counts, and a finishing role
// is stored off. The prototype counted every role, Escalation and finishing ones included,
// which would enable a click the server refuses (a decision recorded in `brief.md`).
//
// EVERY SEND GOES THROUGH THE PAGE'S ONE FLOOD GUARD (`./flood-guard.ts`, #6753): the 700 ms
// debounce, the in-flight drop, the six-per-30-s count and the locks are its, never this
// file's. {@link queueCapability} only builds the change and reads its answer; a door
// failure never rejects the guard's send (its timer calls it unawaited).

import {
  AGENT_MODE_WRITABLE,
  type AgentModeKey,
  agentModeWritable,
  type MachineRoleBlocked,
  type MachineRoleView,
  machineSwitchVerdict,
} from '@developerz/domain';
import type { ChangeVerdict, FloodGuard } from './flood-guard';
import { readFailure } from './job-controls';
import { SEND_ROLE_DESC, SEND_ROLE_LABEL } from './send-work';

/** How long "Saved" shows once a fresh read confirms a change (prototype `:2061`). */
export const SAVED_MS = 1_500;

/** A switch's key in the page's flood guard: one control per machine and role. */
export const capControlKey = (boxId: string, key: AgentModeKey): string =>
  `machine:${boxId}:cap:${key}`;

/** Each switch's name: the card's names (`SEND_ROLE_LABEL`, "Coder" is `worker`). */
export const CAP_NAME_KEY: Readonly<Record<AgentModeKey, string>> = SEND_ROLE_LABEL;

/**
 * Each switch's caption (`title`): the prototype's `CAPS` descriptions, but Gardener's is the
 * platform's own sentence (`agents.job.gardener`); the prototype's "Keeps the repo tidy" names
 * work the gardener does not do (deviation 1).
 */
export const CAP_CAPTION_KEY: Readonly<Record<AgentModeKey, string>> = {
  ...SEND_ROLE_DESC,
  gardener: 'agents.job.gardener',
};

/** The muted line under a blocked role (deviation 2: the prototype has no blocked roles). */
export const CAP_BLOCKED_KEY: Readonly<Record<MachineRoleBlocked, string>> = {
  org_off: 'fleet.card.caps.blocked_org_off',
  cannot_converse: 'fleet.card.caps.blocked_cannot_converse',
  needs_update: 'fleet.card.caps.blocked_needs_update',
};

/** What an answer said about a switch, and when it landed in the page's order. */
export type CapAnswer = { readonly value: boolean; readonly order: number };

/**
 * A sentence under one switch: a refusal, a failure, or what a disagreeing read showed. It
 * stands until the next click on that switch, or until a read later than it shows the role's
 * `machine_enabled` changed from `enabled`, the value it was said against.
 */
export type CapRefusal = {
  readonly text: string;
  readonly order: number;
  readonly enabled: boolean;
};

/** What the client knows about one switch beyond the read. */
export type CapClientState = {
  /** The value a change waiting in the guard, or in flight, asks for. */
  readonly wanted: boolean | undefined;
  /** The change is in flight. */
  readonly saving: boolean;
  /** An answer no fresh read has settled yet. */
  readonly answer: CapAnswer | undefined;
  /** A fresh read confirmed a change within {@link SAVED_MS}. */
  readonly saved: boolean;
  readonly refusal: CapRefusal | undefined;
};

export type CapViewInput = {
  /** The machine's `roles[]`, in the card's order (the server orders them). */
  readonly roles: readonly MachineRoleView[];
  readonly offline: boolean;
  /** The page's controls are locked (`FloodGuard.lockedSeconds() > 0`). */
  readonly locked: boolean;
  readonly client: (key: AgentModeKey) => CapClientState;
};

export type CapSwitchView = {
  readonly key: AgentModeKey;
  readonly nameKey: string;
  readonly captionKey: string;
  /** `aria-checked`: on, or finishing ("on, then off"). */
  readonly checked: boolean;
  /** Shown off while its jobs still run: "then off". */
  readonly finishing: boolean;
  /** The running dot: the role holds jobs here. */
  readonly running: boolean;
  /** How many jobs the role holds here (the confirm's "{n} jobs"). */
  readonly jobs: number;
  /** A change waits in the guard: "Saving…", not yet busy. */
  readonly pending: boolean;
  /** Sent, or answered and not yet confirmed by a fresh read: `aria-busy`, "Saving…". */
  readonly busy: boolean;
  readonly saved: boolean;
  /** `aria-disabled`: busy, or the page is locked. Still focusable. */
  readonly hold: boolean;
  /** The last work role on: disabled, titled "Keep at least one capability on". */
  readonly lastOn: boolean;
  /** `disabled`: the machine is offline, or this is the last work role on. */
  readonly disabled: boolean;
  readonly blockedKey: string | null;
  readonly refusal: string | null;
};

export type CapZoneView = {
  readonly switches: readonly CapSwitchView[];
  /** How many roles hold jobs here. */
  readonly running: number;
  /** How many switches read checked. */
  readonly on: number;
  readonly total: number;
};

/** A switch's stored value: an unsettled answer, else the read. */
export const storedValue = (role: MachineRoleView, c: CapClientState): boolean =>
  c.answer?.value ?? role.machine_enabled;

/** A switch's shown value: a waiting or in-flight change, else its stored value. */
export const shownValue = (role: MachineRoleView, c: CapClientState): boolean =>
  c.wanted ?? storedValue(role, c);

/** Whether turning `key` off would leave no work role on, over the shown values. */
function isLastOn(
  key: AgentModeKey,
  shown: ReadonlyMap<AgentModeKey, boolean>,
  roles: readonly MachineRoleView[],
): boolean {
  if (shown.get(key) !== true || !agentModeWritable('machine', key)) return false;
  const rows = roles.map((r) => ({ mode_key: r.key, enabled: shown.get(r.key) === true }));
  // A key the server serves no row for reads on (`machineSwitchVerdict`'s default): the
  // machine tuple's keys this read left out must not count as on here.
  for (const k of AGENT_MODE_WRITABLE.machine) {
    if (!rows.some((r) => r.mode_key === k)) rows.push({ mode_key: k, enabled: false });
  }
  return machineSwitchVerdict({ rows, modeKey: key, enabled: false }) === 'last_role_on';
}

/** The zone: every switch's state, and the counts its label shows. Pure. */
export function capabilityView(input: CapViewInput): CapZoneView {
  const shown = new Map<AgentModeKey, boolean>(
    input.roles.map((r) => [r.key, shownValue(r, input.client(r.key))]),
  );
  const switches = input.roles.map((role): CapSwitchView => {
    const c = input.client(role.key);
    const on = shown.get(role.key) === true;
    const finishing = !on && role.running > 0;
    const busy = c.saving || c.answer !== undefined;
    const lastOn = isLastOn(role.key, shown, input.roles);
    return {
      key: role.key,
      nameKey: CAP_NAME_KEY[role.key],
      captionKey: CAP_CAPTION_KEY[role.key],
      checked: on || finishing,
      finishing,
      running: role.running > 0,
      jobs: role.running,
      pending: c.wanted !== undefined && !c.saving,
      busy,
      saved: c.saved && !busy,
      hold: busy || input.locked,
      lastOn,
      disabled: input.offline || lastOn,
      blockedKey: role.blocked === null ? null : CAP_BLOCKED_KEY[role.blocked],
      refusal: c.refusal?.text ?? null,
    };
  });
  return {
    switches,
    running: switches.filter((s) => s.running).length,
    on: switches.filter((s) => s.checked).length,
    total: switches.length,
  };
}

/** One switch a fresh read settled. */
export type CapSettled =
  /** The read shows what the answer said: "Saved" and its announcement. */
  | { readonly kind: 'confirmed'; readonly key: AgentModeKey; readonly role: MachineRoleView }
  /** The read shows otherwise: it wins, and the switch says what it shows. */
  | { readonly kind: 'superseded'; readonly key: AgentModeKey; readonly role: MachineRoleView };

/**
 * What a read that started at `readOrder` leaves of the answers and sentences (#6766): every
 * answer older than the read is settled (confirmed or superseded) and dropped; every sentence
 * older than the read whose role's `machine_enabled` changed is dropped. The same maps back
 * when nothing moved. A role absent from the read drops its answer and sentence unsettled.
 * Pure.
 */
export function settleCaps(
  answers: ReadonlyMap<AgentModeKey, CapAnswer>,
  refusals: ReadonlyMap<AgentModeKey, CapRefusal>,
  readOrder: number,
  roles: readonly MachineRoleView[],
): {
  readonly answers: ReadonlyMap<AgentModeKey, CapAnswer>;
  readonly refusals: ReadonlyMap<AgentModeKey, CapRefusal>;
  readonly settled: readonly CapSettled[];
} {
  const roleOf = (k: AgentModeKey) => roles.find((r) => r.key === k);
  let nextAnswers: Map<AgentModeKey, CapAnswer> | null = null;
  const settled: CapSettled[] = [];
  for (const [key, a] of answers) {
    if (readOrder <= a.order) continue;
    nextAnswers ??= new Map(answers);
    nextAnswers.delete(key);
    const role = roleOf(key);
    if (role === undefined) continue;
    settled.push({
      kind: role.machine_enabled === a.value ? 'confirmed' : 'superseded',
      key,
      role,
    });
  }
  let nextRefusals: Map<AgentModeKey, CapRefusal> | null = null;
  for (const [key, r] of refusals) {
    if (readOrder <= r.order) continue;
    const role = roleOf(key);
    if (role !== undefined && role.machine_enabled === r.enabled) continue;
    nextRefusals ??= new Map(refusals);
    nextRefusals.delete(key);
  }
  return { answers: nextAnswers ?? answers, refusals: nextRefusals ?? refusals, settled };
}

/** How a switch's send ended. A 429 has already locked the page when this is reported. */
export type CapOutcome =
  | { readonly kind: 'answered'; readonly value: boolean }
  | { readonly kind: 'refused'; readonly code: string | null }
  | { readonly kind: 'rate_limited'; readonly seconds: number }
  | { readonly kind: 'gone' }
  | { readonly kind: 'failed' };

export type QueueCapability = {
  readonly guard: FloodGuard;
  readonly boxId: string;
  readonly key: AgentModeKey;
  /** The stored value ({@link storedValue}): a run of clicks ending here sends nothing. */
  readonly from: boolean;
  readonly to: boolean;
  /** The write (`setMachineRole`); throws `ApiError` on a refusal. */
  readonly door: (enabled: boolean) => Promise<unknown>;
  readonly onOutcome: (outcome: CapOutcome) => void;
};

/**
 * Queue one switch change through the page's guard: shown at once, sent after its 700 ms.
 * The send never rejects: a 429 locks the page for its `retry_after` here, and every ending
 * is reported to `onOutcome`.
 */
export function queueCapability(o: QueueCapability): ChangeVerdict {
  return o.guard.change<boolean>(capControlKey(o.boxId, o.key), {
    from: o.from,
    to: o.to,
    send: async (enabled) => {
      let outcome: CapOutcome;
      try {
        await o.door(enabled);
        outcome = { kind: 'answered', value: enabled };
      } catch (err) {
        const f = readFailure(err, 'machine_role');
        if (f.kind === 'rate_limited') o.guard.lockFor(f.seconds);
        outcome = f.kind === 'refused' ? { kind: 'refused', code: f.code } : f;
      }
      o.onOutcome(outcome);
    },
  });
}
